import { useMemo, useCallback } from 'react';
import { useMultimodalLive } from './useMultimodalLive';

type AdapterProps = {
  // Match useGeminiLive signature superset, all optional here
  selectedDocument?: string | null;
  activeSessionId?: string | null;
  topicId?: string | null;
  setMessages?: (updater: (prev: any[]) => any[]) => void;
  setUnsavedMessageCount?: (updater: (prev: number) => number) => void;
  messageSeqRef?: React.MutableRefObject<number>;
  getCanvasEl?: (() => HTMLElement | null) | null;
  screenStream?: MediaStream | null;
  getYouTubePlayer?: (() => any) | null;
  currentYouTubeVideoId?: string | null;
  currentSlideContext?: {
    slide: {
      id?: string;
      title?: string | null;
      slide_type?: string | null;
      section?: string | null;
      tutor_prompt?: string | null;
      duration_minutes?: number | null;
      slide_order?: number;
      completed?: boolean;
    } | null;
    lessonTitle?: string;
    progress?: {
      total?: number;
      completed?: number;
      pendingTitles?: string[];
    };
  };
  startCurrentSession?: () => Promise<void>;
  endCurrentSession?: (finalHistory?: any[]) => Promise<void>;
  appendMessage?: (message: any) => void;
  onAgentActivityChange?: (isActive: boolean) => void;
  onBlockAdded?: (data: { block_id: string; block_type: string; title?: string }) => void;
  onBlockUpdated?: (data: { block_id: string }) => void;
  onJournalUpdated?: (data: { journal_id: string; action: string }) => void;
  // Phase 8a: getMessages needed for transcript sync to server
  journalId?: string | null;
  getMessages?: () => any[];
  todosAdded?: string[];
  todosCompleted?: string[];
};

// Phase 8a: Session summarization now handled server-side on WebSocket disconnect
// Frontend just syncs transcript to server periodically via transcript_sync messages

// This adapter returns the same surface as useGeminiLive so ChatPage can switch hooks behind a flag with zero UI changes.
export function useLiveAdapter(props: AdapterProps) {
  const live = useMultimodalLive({
    selectedDocument: props.selectedDocument,
    activeSessionId: props.activeSessionId,
    topicId: props.topicId,
    setMessages: props.setMessages,
    setUnsavedMessageCount: props.setUnsavedMessageCount,
    appendMessage: props.appendMessage,
    messageSeqRef: props.messageSeqRef,
    onAgentActivityChange: props.onAgentActivityChange,
    getCanvasEl: props.getCanvasEl,
    screenStream: props.screenStream,
    getYouTubePlayer: props.getYouTubePlayer,
    currentYouTubeVideoId: props.currentYouTubeVideoId,
    slideContext: props.currentSlideContext,
    onBlockAdded: props.onBlockAdded,
    onBlockUpdated: props.onBlockUpdated,
    onJournalUpdated: props.onJournalUpdated,
    // Phase 8a: Pass getMessages for transcript sync to server
    getMessages: props.getMessages,
    // Cost optimization: Pass journalId for session resumption
    journalId: props.journalId,
  });

  const wrappedConnect = useCallback(async (mode?: string) => {
    try {
      if (props.startCurrentSession) {
        await props.startCurrentSession();
      }
    } catch (e) {
      // ignore; fallback to current active session id
    }
    await live.handleConnectAgent(mode);
  }, [live, props.startCurrentSession]);

  const wrappedStop = useCallback(async () => {
    // Phase 8a: Summarization now handled server-side on disconnect
    // Just clean up the session persistence
    try {
      if (props.endCurrentSession) {
        await props.endCurrentSession();
      }
    } catch (e) {
      // ignore persistence errors; proceed to disconnect anyway
    }
    live.handleStopConversation();
  }, [live, props.endCurrentSession]);

  return useMemo(() => ({
    // State
    agentStatus: live.agentStatus,
    isAgentActive: live.isAgentActive,
    isUserMuted: live.isUserMuted,
    sessionDuration: live.sessionDuration,
    isPushToTalkLocked: live.isPushToTalkLocked,
    isAgentTyping: live.isAgentTyping,
    isUserSpeakingVad: live.isUserSpeakingVad,
    // Cost optimization: Pre-flight state
    isPreFlightReady: live.isPreFlightReady,
    // Handlers
    handleConnectAgent: wrappedConnect,
    handleStopConversation: wrappedStop,
    toggleMute: live.toggleMute,
    handlePushToTalkStart: live.handlePushToTalkStart,
    handlePushToTalkEnd: live.handlePushToTalkEnd,
    handleToggleLock: live.handleToggleLock,
    sendText: live.sendText,
    sendContextUpdate: live.sendContextUpdate,
    // Cost optimization: Pre-flight function
    performPreFlight: live.performPreFlight,
    // Infinite sessions: Session extension indicator
    isExtendingSession: live.isExtendingSession,
    // Expose raw live ref if needed later
    _live: live,
    audioManager: live.audioManager,
  }), [live, wrappedConnect, wrappedStop]);
}
